import { ResultConstant } from '@enums/result-constant.enum';
import { ExecutionTriggeredType } from '@enums/triggered-type.enum';
import { FilterTimePeriod } from '@modules/dashboard/enums/filter-time-period.enum';
import { OptionTypes } from '@modules/dashboard/enums/option-types.enum';
import { DateObject } from '@shared/components/filters/types';

export const FILTER_KEYS = {
  TEST_CASE: 'TEST_CASE',
  TEST_SUITE: 'TEST_SUITE',
  TEST_MACHINE: 'TEST_MACHINE',
  RUN_HISTORY: 'RUN_HISTORY',
  BASE_FILTERS_KEY: 'runResultsFilters',
};

export const KEYS = {
  SELECTED_RUN_RESULT: 'result', // common section
  SELECTED_TEST_SUITE: 'suiteId', // common section
  SELECTED_TEST_MACHINES: 'environmentResultId', // common section
  CREATED_BY_USER_IDS: 'createdById', // common section
  CREATED_DATE_OBJECT: 'createdAt', // common section
  UPDATED_DATE_OBJECT: 'updatedAt', // common section
  SELECTED_TAG_IDS: 'tagId', // common section

  SELECTED_REQUIREMENT_TYPE: 'requirementTypeId', // not common
  SELECTED_TEST_MACHINES_ENV: 'id', // not common
  SELECTED_REQUIREMENT: 'requirementId', // not common
  SELECTED_SCENARIO: 'scenarioId', // not common
  SELECTED_LINKED_TO: 'linkedTo', // not common
  SELECTED_ASSIGNEE: 'assignee', // not common
  SELECTED_PRIORITY: 'priorityId', // not common
  SELECTED_REVIEWER: 'reviewedById', // not common
  SELECTED_TYPE: 'testCaseTypeId', // not common

  SELECTED_LAST_TIME_PERIOD: 'startTime', // not common
  SELECTED_TRIGGERED_TYPE: 'triggeredType', // not common

  SELECTED_VISUAL_RESULT: 'consolidatedVisualResult', //not common
};

export const testCaseSections = () => [
  KEYS.SELECTED_PRIORITY,
  KEYS.SELECTED_LINKED_TO,
  KEYS.SELECTED_VISUAL_RESULT,
  KEYS.SELECTED_ASSIGNEE,
  KEYS.SELECTED_REQUIREMENT_TYPE,
  KEYS.SELECTED_REQUIREMENT,
  KEYS.SELECTED_SCENARIO,
  KEYS.SELECTED_REVIEWER,
  KEYS.SELECTED_TEST_MACHINES,
  KEYS.SELECTED_TYPE,
];

export const getCommonSections = () => [
  KEYS.SELECTED_RUN_RESULT, // common section
  KEYS.SELECTED_TEST_SUITE, // common section
  KEYS.CREATED_DATE_OBJECT, // common section
  KEYS.UPDATED_DATE_OBJECT, // common section
  KEYS.CREATED_BY_USER_IDS, // common section
  KEYS.SELECTED_TAG_IDS, // common section
];

export const testSuiteSections = () => [
  KEYS.SELECTED_TEST_MACHINES,
  KEYS.SELECTED_LINKED_TO,
];
export const testMachineSections = () => [KEYS.SELECTED_TEST_MACHINES_ENV];
export const runHistorySections = () => [
  KEYS.SELECTED_RUN_RESULT,
  KEYS.SELECTED_TRIGGERED_TYPE,
  KEYS.SELECTED_LAST_TIME_PERIOD,
];
export const INITIAL_FORM_VALUES = {
  [KEYS.SELECTED_RUN_RESULT]: [],
  [KEYS.SELECTED_VISUAL_RESULT]: [],
  [KEYS.SELECTED_TEST_SUITE]: [],
  [KEYS.SELECTED_LINKED_TO]: [],
  [KEYS.SELECTED_TEST_MACHINES]: [],
  [KEYS.SELECTED_TEST_MACHINES_ENV]: [],
  [KEYS.SELECTED_ASSIGNEE]: [],
  [KEYS.SELECTED_PRIORITY]: [],
  [KEYS.CREATED_BY_USER_IDS]: [],
  [KEYS.SELECTED_REQUIREMENT_TYPE]: [],
  [KEYS.SELECTED_REQUIREMENT]: [],
  [KEYS.SELECTED_SCENARIO]: [],
  [KEYS.SELECTED_REVIEWER]: [],
  [KEYS.SELECTED_LAST_TIME_PERIOD]: [],
  [KEYS.SELECTED_TRIGGERED_TYPE]: [],
  [KEYS.SELECTED_TYPE]: [],
  [KEYS.CREATED_DATE_OBJECT]: {
    key: 'createdAtEpoch',
    value: [],
    option: FilterTimePeriod.CUSTOM,
  } as DateObject,
  [KEYS.UPDATED_DATE_OBJECT]: {
    key: 'updatedAtEpoch',
    value: [],
    option: FilterTimePeriod.CUSTOM,
  } as DateObject,
  [KEYS.SELECTED_TAG_IDS]: [],
};

const INITIAL_FORM_ERRORS = {};

export const ACTION_TYPES = {
  UPDATE_FORM_VALUES: 'updateFormValues',
  RESET_FORM_VALUES: 'resetFormValues',
  RESET_SELECTED_VALUE: 'resetSelectedValue',
  CLEAR_ALL_VALUES: 'clearAllValues',
};

export const TIME_PERIOD_OPTIONS = [
  {
    id: FilterTimePeriod.TODAY,
    name: 'common.time.today',
  },
  {
    id: FilterTimePeriod.LAST_SEVEN_DAYS,
    name: 'common.time.week',
  },
  {
    id: FilterTimePeriod.LAST_30_DAYS,
    name: 'common.time.month',
  },
  {
    id: FilterTimePeriod.LAST_90_DAYS,
    name: 'common.time.months',
  },
];
export const TRIGGERED_FILTER_OPTIONS = [
  {
    id: ExecutionTriggeredType.MANUAL,
    name: 'test-suite.type.manual',
  },
  {
    id: ExecutionTriggeredType.SCHEDULED,
    name: 'test-suite.type.scheduled',
  },
  {
    id: ExecutionTriggeredType.API,
    name: 'test-suite.type.api',
  },
];

export const VISUAL_RESULT_FILTER_OPTIONS = [
  {
    id: ResultConstant.SUCCESS,
    name: 'execution.result.SUCCESS',
  },
  {
    id: ResultConstant.FAILURE,
    name: 'execution.result.FAILED',
  },
];

export const RESULT_FILTER_OPTIONS = [
  {
    id: ResultConstant.SUCCESS,
    name: 'execution.result.SUCCESS',
  },
  {
    id: ResultConstant.FAILURE,
    name: 'execution.result.FAILED',
  },
  {
    id: ResultConstant.STOPPED,
    name: 'execution.result.STOPPED',
  },
  {
    id: ResultConstant.QUEUED,
    name: 'execution.result.QUEUED',
  },
  {
    id: ResultConstant.NOT_EXECUTED,
    name: 'execution.result.NOT_EXECUTED',
  },
  {
    id: ResultConstant.RUNNING,
    name: 'execution.result.RUNNING',
  },
];

export const LINKED_TO_FILTER_OPTIONS = [
  {
    id: 'TEST_MANAGEMENT',
    name: 'linked.to.Test_Management',
  },
  {
    id: 'BUG_REPORTING',
    name: 'linked.to.Bug_Reporting',
  },
];

export const keyFieldMap = {
  [KEYS.SELECTED_VISUAL_RESULT]: {
    label: 'common.default.field.label.visualResult',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_RUN_RESULT]: {
    label: 'common.default.field.label.new.result',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_TEST_SUITE]: {
    label: 'common.test_suites',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_TEST_MACHINES]: {
    label: 'common.test_machines',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.CREATED_BY_USER_IDS]: {
    label: 'test_plan.execution.filter.created_by',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.CREATED_DATE_OBJECT]: {
    label: 'test_plan.execution.filter.created_date',
    type: OptionTypes.TYPE_DATE,
  },
  [KEYS.UPDATED_DATE_OBJECT]: {
    label: 'test_plan.execution.filter.updated_date',
    type: OptionTypes.TYPE_DATE,
  },
  [KEYS.SELECTED_TAG_IDS]: {
    label: 'common.default.field.label.tagId',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_REQUIREMENT_TYPE]: {
    label: 'common.requirement.type',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_TYPE]: {
    label: 'common.default.field.label.type',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_TEST_MACHINES_ENV]: {
    label: 'common.test_machines',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_REQUIREMENT]: {
    label: 'common.requirement',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_SCENARIO]: {
    label: 'common.scenario.label',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_LINKED_TO]: {
    label: 'common.linked',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_ASSIGNEE]: {
    label: 'common.assignee',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_PRIORITY]: {
    label: 'common.priortity',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_REVIEWER]: {
    label: 'common.reviewer',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_LAST_TIME_PERIOD]: {
    label: 'run.details.filter_time_period',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  [KEYS.SELECTED_TRIGGERED_TYPE]: {
    label: 'result.triggered',
    type: OptionTypes.TYPE_CHECKBOX,
  },
  updatedAtEpoch: {
    label: 'test_plan.execution.filter.updated_date',
    type: OptionTypes.TYPE_DATE,
  },
  createdAtEpoch: {
    label: 'test_plan.execution.filter.created_date',
    type: OptionTypes.TYPE_DATE,
  },
};

export const dateKeys = {
  updatedAtEpoch: 'updatedAtEpoch',
  createdAtEpoch: 'createdAtEpoch',
};
